/**
 * „Na čem a z čeho vyrábíte?" — machines and materials per picked operation
 * (KAN-1473). Moved verbatim out of ``OnboardingStart``.
 */

import { IngotIcon } from "@/ingot";
import { useTranslation } from "react-i18next";

import { cx, formatDecimal } from "@/lib/utils";

import { MachinePicker } from "./MachinePicker";
import { axisValuesOf, offeredMaterialCodes, withOfferedMaterials } from "./model";
import { PickCard } from "./PickCard";
import { stepHeading as heading } from "./StepHeading";
import { toggleIn as toggle, type WizardSelection } from "./useWizardSelection";

export function BlocksStep({
  w,
  allowCustom = true,
}: {
  w: WizardSelection;
  /** „Založit vlastní" stroj — off where there is no tenant to own it. */
  allowCustom?: boolean;
}) {
  const { t, i18n } = useTranslation("admin");
  const { chosen, startEmpty, choices, patchChoice } = w;
  return (
    <>
      {heading(
        t("onboardingStart.q.blocks"),
        t("onboardingStart.sub.blocks"),
      )}
      {chosen.length === 0 && startEmpty ? (
        <div className="zero" data-testid="onboarding-blocks-skipped">
          <IngotIcon name="info" size={18} />
          <div>
            <b>{t("onboardingStart.skipped")}</b>
            <div className="t-small">
              {t("onboardingStart.blocks.skippedText")}
            </div>
          </div>
        </div>
      ) : chosen.length === 0 ? (
        <div className="zero">
          <IngotIcon name="info" size={18} />
          <div className="t-small">{t("onboardingStart.blocks.none")}</div>
        </div>
      ) : (
        chosen.map((op) => {
          const choice = choices[op.slug];
          if (!choice) return null;
          const machines = choice.machines
            .filter((e) => e.machineKey !== "")
            .reduce((n, e) => n + e.quantity, 0);
          const materials = choice.materialCodes.length;
          // KAN-1323: only what the selected machines can process.
          const hasMachine = choice.machines.some(
            (e) => e.machineKey !== "",
          );
          const offered = offeredMaterialCodes(op, choice.machines);
          const visible = op.materials.filter((m) => offered.has(m.code));
          const hidden = op.materials.length - visible.length;
          return (
            <section
              key={op.slug}
              className="opblock"
              data-testid={`onboarding-blocks-${op.slug}`}
            >
              <header className="opblock-head">
                <h3>{op.display_name}</h3>
                <span className={cx("tag", machines ? "tag-ok" : "tag-warn")}>
                  {machines
                    ? t("onboardingStart.tags.machines", { count: machines })
                    : t("onboardingStart.tags.noMachine")}
                </span>
                <span className={cx("tag", materials ? "tag-ok" : "tag-warn")}>
                  {materials
                    ? t("onboardingStart.tags.materials", { count: materials })
                    : t("onboardingStart.tags.noMaterial")}
                </span>
              </header>
              <div className="opblock-body">
                <div className="sect-label">
                  {t("onboardingStart.q.machine")}
                </div>
                <MachinePicker
                  allowCustom={allowCustom}
                  op={op}
                  choice={choice}
                  onPatch={(patch) =>
                    // Removing a machine (or unticking a chip of
                    // one) unticks what no machine processes now.
                    patchChoice(
                      op.slug,
                      withOfferedMaterials(op, { ...choice, ...patch }),
                    )
                  }
                />

                <div className="sect-label" style={{ marginTop: 22 }}>
                  {t("onboardingStart.q.materials")}
                </div>
                {!hasMachine ? (
                  <div
                    className="zero"
                    data-testid={`onboarding-materials-no-machine-${op.slug}`}
                  >
                    <IngotIcon name="info" size={18} />
                    <div className="t-small">
                      {t("onboardingStart.materials.pickMachineFirst")}
                    </div>
                  </div>
                ) : (
                  <>
                    <p className="q-sub" style={{ marginBottom: 12 }}>
                      {t("onboardingStart.materials.lead", {
                        selected: materials,
                        total: visible.length,
                      })}
                      {hidden > 0 ? (
                        <span
                          data-testid={`onboarding-materials-hidden-${op.slug}`}
                        >
                          {" "}
                          {t("onboardingStart.materials.hidden", {
                            count: hidden,
                          })}
                        </span>
                      ) : null}
                    </p>
                    <div
                      className="pick-grid tight"
                      role="group"
                      aria-label={t("onboardingStart.q.materials")}
                    >
                      {visible.map((m) => (
                        <PickCard
                          key={m.code}
                          on={choice.materialCodes.includes(m.code)}
                          onClick={() =>
                            patchChoice(op.slug, {
                              materialCodes: toggle(
                                choice.materialCodes,
                                m.code,
                              ),
                            })
                          }
                          title={m.display_name}
                          sub={m.note ?? undefined}
                          testId={`onboarding-material-${op.slug}-${m.code}`}
                        />
                      ))}
                    </div>
                    {materials === 0 ? (
                      <p
                        className="t-small is-warn"
                        style={{ marginTop: 10 }}
                      >
                        {t("onboardingStart.materials.none")}
                      </p>
                    ) : null}
                    {(() => {
                      // KAN-2012 bod 6 — „Jaké tloušťky zpracujete?" Seed
                      // založí jen varianty v rozsahu, takže ceník i web
                      // obchodu nabízejí jen to, co dílna opravdu umí.
                      const values = axisValuesOf(op, choice.materialCodes);
                      if (values.length < 2) return null;
                      const unit = op.variant_axis?.unit ? ` ${op.variant_axis.unit}` : "";
                      const label = (v: number) => `${formatDecimal(v, i18n.language)}${unit}`;
                      const lo = choice.axisRange?.min ?? values[0];
                      const hi = choice.axisRange?.max ?? values[values.length - 1];
                      const setRange = (min: number, max: number) =>
                        patchChoice(op.slug, {
                          axisRange: { min: Math.min(min, max), max: Math.max(min, max) },
                        });
                      const inRange = values.filter((v) => v >= lo && v <= hi).length;
                      return (
                        <div
                          className="stack"
                          style={{ gap: 8, marginTop: 18 }}
                          data-testid={`onboarding-axis-range-${op.slug}`}
                        >
                          <div className="sect-label">
                            {t("onboardingStart.axisRange.title")}
                          </div>
                          <p className="q-sub">{t("onboardingStart.axisRange.lead")}</p>
                          <div className="row" style={{ gap: 10, alignItems: "center" }}>
                            <label className="row" style={{ gap: 6, alignItems: "center" }}>
                              <span className="t-small">{t("onboardingStart.axisRange.from")}</span>
                              <select
                                className="input"
                                value={String(lo)}
                                onChange={(e) => setRange(Number(e.target.value), hi)}
                                data-testid={`onboarding-axis-min-${op.slug}`}
                              >
                                {values.map((v) => (
                                  <option key={v} value={String(v)}>
                                    {label(v)}
                                  </option>
                                ))}
                              </select>
                            </label>
                            <label className="row" style={{ gap: 6, alignItems: "center" }}>
                              <span className="t-small">{t("onboardingStart.axisRange.to")}</span>
                              <select
                                className="input"
                                value={String(hi)}
                                onChange={(e) => setRange(lo, Number(e.target.value))}
                                data-testid={`onboarding-axis-max-${op.slug}`}
                              >
                                {values.map((v) => (
                                  <option key={v} value={String(v)}>
                                    {label(v)}
                                  </option>
                                ))}
                              </select>
                            </label>
                          </div>
                          <p className="t-small" data-testid={`onboarding-axis-summary-${op.slug}`}>
                            {t("onboardingStart.axisRange.summary", {
                              count: inRange,
                              total: values.length,
                            })}{" "}
                            {t("onboardingStart.axisRange.format")}
                          </p>
                        </div>
                      );
                    })()}
                  </>
                )}
              </div>
            </section>
          );
        })
      )}
    </>
  );
}
